import { useMemo } from 'react';
import { formatBRL } from '../_shared/utils/format';
import { useChartTheme } from '../../../components/ui';

/**
 * Linha de crescimento acumulado (base de clientes + faturamento) — porte
 * do web novo do prospect (Fase 27), restilizado pro slate dark do painel.
 */
interface Props {
  /** YYYY-MM ordenados cronologicamente */
  months: string[];
  /** Base de clientes acumulada, alinhada a `months` */
  clientsCumulative: number[];
  /** Faturamento acumulado, alinhado a `months` */
  revenueCumulative: number[];
}

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function ymToShort(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym);
  if (!m) return ym;
  return MONTH_LABELS[Number(m[2]) - 1] ?? ym;
}

function compactBRL(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1).replace('.', ',')}k`;
  return String(Math.round(v));
}

const VB_W = 700;
const VB_H = 220;
const PAD_X = 30;
const PAD_TOP = 24;
const PAD_BOTTOM = 34;

export default function GrowthLineChart({ months, clientsCumulative, revenueCumulative }: Props) {
  const chart = useChartTheme();
  const CLIENTS_COLOR = chart.primary;
  const REVENUE_COLOR = chart.warning;

  const { clientPts, revenuePts, xs } = useMemo(() => {
    const plotW = VB_W - PAD_X * 2;
    const plotH = VB_H - PAD_TOP - PAD_BOTTOM;
    const stepX = months.length > 1 ? plotW / (months.length - 1) : 0;
    const xs = months.map((_, i) => PAD_X + (months.length > 1 ? stepX * i : plotW / 2));

    // Escalas independentes: cada série normalizada pelo próprio máximo
    const maxClients = Math.max(1, ...clientsCumulative);
    const maxRevenue = Math.max(1, ...revenueCumulative);
    const clientPts = xs.map((x, i) => ({
      x, y: PAD_TOP + plotH - ((clientsCumulative[i] ?? 0) / maxClients) * plotH,
    }));
    const revenuePts = xs.map((x, i) => ({
      x, y: PAD_TOP + plotH - ((revenueCumulative[i] ?? 0) / maxRevenue) * plotH,
    }));
    return { clientPts, revenuePts, xs };
  }, [months, clientsCumulative, revenueCumulative]);

  const lastClients = clientsCumulative[clientsCumulative.length - 1] ?? 0;
  const lastRevenue = revenueCumulative[revenueCumulative.length - 1] ?? 0;
  const labelEvery = months.length > 8 ? 2 : 1;

  const toPolyline = (pts: Array<{ x: number; y: number }>) =>
    pts.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <div>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Crescimento da base e do faturamento no ano"
      >
        {/* Grade horizontal */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={PAD_X} x2={VB_W - PAD_X}
            y1={PAD_TOP + (VB_H - PAD_TOP - PAD_BOTTOM) * f}
            y2={PAD_TOP + (VB_H - PAD_TOP - PAD_BOTTOM) * f}
            stroke={chart.grid}
            strokeOpacity={0.5}
            strokeWidth={1}
          />
        ))}

        {/* Linhas */}
        <polyline points={toPolyline(revenuePts)} fill="none" stroke={REVENUE_COLOR} strokeWidth={2.5} strokeLinejoin="round" />
        <polyline points={toPolyline(clientPts)} fill="none" stroke={CLIENTS_COLOR} strokeWidth={2.5} strokeLinejoin="round" />

        {/* Pontos + labels */}
        {clientPts.map((p, i) => (
          <g key={`c-${i}`}>
            <circle cx={p.x} cy={p.y} r={4} fill={CLIENTS_COLOR} />
            {(i % labelEvery === 0 || i === clientPts.length - 1) && (
              <text x={p.x} y={p.y - 9} textAnchor="middle" fontSize={11} fontWeight={700} fill={CLIENTS_COLOR}>
                {clientsCumulative[i]}
              </text>
            )}
          </g>
        ))}
        {revenuePts.map((p, i) => (
          <g key={`r-${i}`}>
            <circle cx={p.x} cy={p.y} r={4} fill={REVENUE_COLOR} />
            {(i % labelEvery === 0 || i === revenuePts.length - 1) && (
              <text x={p.x} y={p.y + 17} textAnchor="middle" fontSize={11} fontWeight={700} fill={REVENUE_COLOR}>
                {compactBRL(revenueCumulative[i] ?? 0)}
              </text>
            )}
          </g>
        ))}

        {/* Labels dos meses */}
        {months.map((ym, i) => (
          <text
            key={`lbl-${ym}`}
            x={xs[i]} y={VB_H - 10}
            textAnchor="middle" fontSize={11}
            fill={chart.axis}
          >
            {ymToShort(ym)}
          </text>
        ))}
      </svg>

      {/* Legenda com totais atuais */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 px-1">
        <span className="flex items-center gap-1.5 text-xs text-fg-2">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: CLIENTS_COLOR }} />
          Base de clientes
          <b style={{ color: CLIENTS_COLOR }}>{lastClients}</b>
        </span>
        <span className="flex items-center gap-1.5 text-xs text-fg-2">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: REVENUE_COLOR }} />
          Faturamento
          <b style={{ color: REVENUE_COLOR }}>{formatBRL(lastRevenue)}</b>
        </span>
      </div>
    </div>
  );
}
